import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { outputSurface } from '../../theme';
import { BookedStay, FixedPoint, Journey, TripBasics, createTrip } from '../../lib/plan';
import { BookingResult, tripOutline } from '../../lib/booking';
import TripBasicsForm, { basicsProblem, cleanBasics, emptyBasics, suggestTitle } from './TripBasicsForm';
import BookingDialog from './BookingDialog';

export default function NewTripPage() {
  const navigate = useNavigate();
  const [basics, setBasics] = useState(emptyBasics);
  const [fixed, setFixed] = useState<FixedPoint[]>([]);
  const [titleTouched, setTitleTouched] = useState(false);
  const [paste, setPaste] = useState('');
  const [stays, setStays] = useState<BookedStay[]>([]);
  const [journey, setJourney] = useState<Journey | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [fromBooking, setFromBooking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem = basicsProblem(basics);
  const hasPaste = paste.trim().length > 0;

  async function create(b: TripBasics, f: FixedPoint[], st: BookedStay[], j: Journey | null): Promise<string | null> {
    setBusy(true);
    setError(null);
    const { tripId, error: err } = await createTrip(cleanBasics(b), f, st, j);
    setBusy(false);
    if (!tripId) {
      const message = err ?? 'The trip could not be saved. Try again.';
      setError(message);
      return message;
    }
    // A day-generation error after the trip exists is recoverable from the
    // board (saving the basics again re-syncs), so go on regardless. A pasted
    // list goes to review first: nothing from it lands unchecked.
    navigate(hasPaste ? `/trips/${tripId}/paste` : `/trips/${tripId}`, {
      replace: true,
      state: hasPaste ? { text: paste } : undefined,
    });
    return null;
  }

  function handleCreate() {
    if (problem) return;
    void create(basics, fixed, stays, journey);
  }

  /**
   * A booking fills the form door to door: start when the first leg leaves,
   * finish when the last lands back home. The legs themselves are added when
   * the trip is created.
   */
  function readBooking(r: BookingResult) {
    const o = tripOutline(r.legs);
    const next = { ...basics };
    if (r.destination && !next.destination?.trim()) next.destination = r.destination;
    // A stays-only booking has no legs: its dates are check-in and check-out.
    const firstIn = r.stays.map((x) => x.check_in).sort()[0];
    const lastOut = r.stays.map((x) => x.check_out).sort().slice(-1)[0];
    const start = o.start ?? (firstIn ? { date: firstIn, time: null, place: r.stays[0].town, mode: null, detail: null } : null);
    const end = o.end ?? (!r.legs.length && lastOut ? { date: lastOut, time: null, place: r.stays.slice(-1)[0].town, mode: null, detail: null } : null);
    if (start) {
      next.start_date = start.date;
      next.start_place = start.place;
      next.start_mode = start.mode;
      next.start_detail = start.detail ?? '';
      next.start_time = start.time;
    }
    if (end) {
      next.end_date = end.date;
      next.end_place = end.place;
      next.end_mode = end.mode;
      next.end_detail = end.detail ?? '';
      next.end_time = end.time;
    }
    if (!titleTouched) next.title = suggestTitle(next);
    const summary = [
      start || end ? 'start and finish' : null,
      r.legs.length ? `${r.legs.length} ${r.legs.length === 1 ? 'leg' : 'legs'}` : null,
      o.layovers.length ? `${o.layovers.length} ${o.layovers.length === 1 ? 'layover' : 'layovers'}` : null,
      r.stays.length ? `${r.stays.length} ${r.stays.length === 1 ? 'stay' : 'stays'}` : null,
    ].filter(Boolean);
    return {
      basics: next,
      fixed: [...fixed.filter((f) => !f.booked), ...o.arrivals],
      stays: r.stays,
      journey: r.legs.length ? { legs: r.legs, transitNights: o.transitNights } : null,
      summary: summary.join(', '),
    };
  }

  /** "Edit first": fill the form and let the traveller check it. */
  function applyBooking(r: BookingResult) {
    const f = readBooking(r);
    setBookingOpen(false);
    setBasics(f.basics);
    setFixed(f.fixed);
    setStays(f.stays);
    setJourney(f.journey);
    setFromBooking(f.summary);
    return f;
  }

  /**
   * "Create trip" straight from the booking. Filling the form and leaving the
   * save button at the bottom of a long page lost trips: people took the filled
   * form for a made trip. If the booking leaves something required open (a
   * one-way ticket has no finish), the form opens at that question instead.
   */
  async function createFromBooking(r: BookingResult): Promise<string | null> {
    const f = applyBooking(r);
    const missing = basicsProblem(f.basics);
    if (missing) {
      setError(`${missing} The booking didn’t say — fill it in below, then create the trip.`);
      return null;
    }
    return create(f.basics, f.fixed, f.stays, f.journey);
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Button component={RouterLink} to="/trips" size="small" sx={{ ml: -1, mb: 1 }}>
        ← Your trips
      </Button>
      <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
        A new trip
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 620 }}>
        Start from where and when. The days are laid out from the dates; how you get anywhere can be decided later.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 4,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: fromBooking ? 'primary.light' : 'divider',
          bgcolor: fromBooking ? 'rgba(26,58,42,0.05)' : 'background.paper',
        }}
      >
        <ConfirmationNumberOutlinedIcon sx={{ color: 'primary.main' }} />
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600 }}>{fromBooking ? 'Filled from your booking' : 'Already booked?'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {fromBooking
              ? `Roamoir filled in ${fromBooking}. Check it below; booked stays pin their nights.`
              : 'Drop a screenshot of your itinerary or paste the confirmation, and Roamoir fills in the start and finish.'}
          </Typography>
        </Box>
        <Button variant={fromBooking ? 'text' : 'outlined'} onClick={() => setBookingOpen(true)}>
          {fromBooking ? 'Read another' : 'Fill from a booking'}
        </Button>
      </Box>
      <BookingDialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        onApply={(r) => void applyBooking(r)}
        onCreate={createFromBooking}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '999 1 560px', minWidth: 0 }}>
          <TripBasicsForm
            value={basics}
            onChange={setBasics}
            titleTouched={titleTouched}
            onTitleTouched={() => setTitleTouched(true)}
            fixed={fixed}
            onFixedChange={setFixed}
          />
        </Box>

        <Box
          component="aside"
          sx={{
            flex: '1 1 340px',
            minWidth: 0,
            bgcolor: outputSurface.bg,
            color: outputSurface.text,
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContentPasteIcon fontSize="small" />
            <Typography variant="h6" sx={{ color: '#FFFFFF', fontSize: '1.1rem' }}>
              Already have a list?
            </Typography>
            <Typography variant="caption" sx={{ ml: 'auto', color: outputSurface.muted }}>
              Optional
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: outputSurface.muted, lineHeight: 1.6 }}>
            Paste a plan from ChatGPT, a note or an email: a full day-by-day itinerary, or just your must-sees. Roamoir pulls
            out the places as candidates, and you check them before anything is added.
          </Typography>
          <TextField
            multiline
            minRows={10}
            maxRows={22}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={'Uffizi\nBrancacci Chapel\nSiena Cathedral\nA hike in the Val d’Orcia\n…'}
            slotProps={{
              input: {
                sx: {
                  bgcolor: '#0E2419',
                  color: outputSurface.text,
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                  fontSize: '0.8rem',
                  lineHeight: 1.6,
                },
              },
            }}
          />
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
        <Button variant="contained" size="large" onClick={handleCreate} disabled={!!problem || busy}>
          {busy ? <CircularProgress size={22} color="inherit" /> : hasPaste ? 'Create trip and read my list' : 'Create trip'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {problem ?? (hasPaste ? 'You’ll review the candidates before they’re added.' : 'Empty days, ready for candidates.')}
        </Typography>
      </Box>
    </Box>
  );
}
